import {Component, OnInit} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {first} from 'rxjs/operators';
import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';
import {ListView, TypeComparaison, TypeFilter} from '@domain/common/list-view';
import {FloatingButtonAction, TypeAction} from '@share/component/floating-button/floating-button';
import {ListViewService} from '@share/component/list-view/list-view.service';
import {Relance} from '@domain/relance/relance';
import {TypeCodeErreur} from '@domain/common/http/result';
import {NotificationRelancesService} from './notification-relances.service';
import {RelanceListItemComponent} from './relance-list-item/relance-list-item.component';
import {Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {AppState} from '@domain/appstate';
import {User, TypeProfil} from '@domain/user/user';
import {filterFirstNotNull} from '@share/utils/rxjs-custom-operator';
import {TypePortee} from '@domain/workflow/workflow';


/**
 * Onglet notification relances
 *
 * @author Guillaume TRAVOT
 * @date 21/11/2023
 */
@Component({
    host: {'data-test-id': 'notification-relances'},
    templateUrl: './notification-relances.component.html'
})
export class NotificationRelancesComponent implements OnInit {

    /** Liste des actions */
    listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

    /** Liste "ListView" des relances */
    listeRelances: ListView<Relance, RelanceListItemComponent>;

    /** Utilisateur connecté (droits) */
    user: User;

     /** Indique si l'utilisateur est sous-admin */
    get estSousAdmin(): boolean {
        return this.user?.fonction === TypeProfil.SOUS_ADMINISTRATEUR;
    }

    /**
     * Constructeur
     *
     * @param translateService  Service de traduction
     * @param toastrService     Service de notification
     * @param notificationRelancesService Service de notification relances
     * @param listViewService Service de liste
     * @param router Service de navigation
     * @param store Service de stockage
     */
    constructor(private translateService: TranslateService,
                private toastrService: ToastrService,
                private notificationRelancesService: NotificationRelancesService,
                private listViewService: ListViewService<Relance, RelanceListItemComponent>,
                private router: Router,
                private store: Store<AppState>) {
    }


    /**
     * Initialisation du composant
     */
    ngOnInit(): void {
        //Initialisation de la ListView
        this.listeRelances = new ListView<Relance, RelanceListItemComponent>({
            uri: '/controller/Relance/listeRelances',
            title: this.translateService.instant('admin.bibliotheque.notifications.relances.title'),
            component: RelanceListItemComponent,
            isFilter: true,
            isSearchBar: true,
            isFilterVisible: true,
            showPagination: true,
            extraOptions: {
                //Indique si l'utilisateur est un sous-admin
                isSousAdmin: false
            },
            mapResult: (page: any) => {
                //Conversion JSON => instances de Relance
                if (page?.listeResultats) {
                    page.listeResultats = (page.listeResultats ?? []).map((r: any) => Object.assign(new Relance(), r));
                }
                return page;
            },
            listeFilters: [
                {
                    clef: 'idPortee',
                    title: this.translateService.instant('admin.bibliotheque.notifications.relances.portee'),
                    listeValues: [
                        {code: TypePortee.AV, libelle: this.translateService.instant('portee.AV')},
                        {code: TypePortee.DV, libelle: this.translateService.instant('portee.DV')},
                        {code: TypePortee.FC, libelle: this.translateService.instant('portee.FC')},
                        {code: TypePortee.NF, libelle: this.translateService.instant('portee.NF')},
                        {code: TypePortee.OD, libelle: this.translateService.instant('portee.OD')},
                        {code: TypePortee.OT, libelle: this.translateService.instant('portee.OT')}
                    ]
                },
                {
                    clef: 'messageNotif.titre',
                    title: this.translateService.instant('admin.bibliotheque.notifications.relances.message'),
                    typeComparaison: TypeComparaison[TypeComparaison.LIKE],
                    isDefault: true,
                    type: TypeFilter[TypeFilter.STRING]
                },
                {
                    clef: 'isActif',
                    title: this.translateService.instant('admin.bibliotheque.notifications.relances.actif'),
                    typeComparaison: TypeComparaison[TypeComparaison.EQUAL],
                    type: TypeFilter[TypeFilter.BOOLEAN],
                    isDefault: true,
                    onloadValue: 'true'
                }
            ]
        });

        //Récupération utilisateur connecté (droits)
        this.store.select(state => state.session?.user).pipe(filterFirstNotNull()).subscribe(user => {
            //Stockage utilisateur
            this.user = user;

            this.listeRelances.extraOptions = {
                //Indique si l'utilisateur est un sous-admin
                isSousAdmin: this.estSousAdmin
            };

            //Initialisation des actions
            this.defineListeActions();
        });

        //Persistence des filtres
        this.listeRelances.isPersistFilters = true;
        this.listeRelances.className = 'NotificationRelancesComponent';


    }

    /**
     * Définit les actions disponibles sur l’écran
     */
    private defineListeActions(): void {
        //Déclaration des actions disponibles selon l’état de sélection
        this.listeActions.next([
            {
                //Création d'une relance
                type: TypeAction.PRIMARY,
                icone: 'nio icon-ajouter',
                libelle: 'global.actions.ajouter',
                doAction: () => this.createNewRelance(),
                isVisible: () => !this.estSousAdmin
            },{
                //Sélectionner tous les éléments affichés
                type: TypeAction.SECONDARY,
                icone: 'nio icon-tout_selectionner',
                libelle: 'liste.actions.selectionnerElementsAffiches',
                doAction: () => this.selectAll(true),
                isDisabled: () => !this.listeRelances?.data?.listeResultats?.length || this.isAllSelected(),
                isVisible: () => this.hasSelection()
            },
            {
                //Désélectionner tous les éléments affichés
                type: TypeAction.SECONDARY,
                icone: 'nio icon-annuler_selection',
                libelle: 'liste.actions.toutDeselectionner',
                doAction: () => this.selectAll(false),
                isDisabled: () => !this.listeRelances?.data?.listeResultats?.length || !this.hasSelection(),
                isVisible: () => this.hasSelection()
            },
            {
                //Exécuter les relances sélectionnées
                type: TypeAction.PRIMARY,
                icone: 'material-symbols-rounded',
                iconeName: 'rule_settings',
                libelle: 'global.actions.executer',
                doAction: () => this.executeSelection(),
                isDisabled: () => !this.hasSelection(),
                isVisible: () => this.hasSelection()
            }
        ]);
    }

    /**
     * Création d'une nouvelle relance
     */
    createNewRelance() {
        this.router.navigate(['/Admin/Bibliotheque/Notification/Relances', 0]);
    }

    /**
     * Sélectionne ou désélectionne toutes les relances
     *
     * @param isSelected true pour sélectionner, false pour désélectionner
     */
    private selectAll(isSelected: boolean): void {
        //On délègue à la ListView pour maintenir nbSelectedItems synchronisé avec l'état de sélection
        this.listeRelances?.selectAll(isSelected);
    }

    /**
     * Indique si au moins une relance est sélectionnée.
     *
     * @return true si une sélection existe, sinon false
     */
    private hasSelection(): boolean {
        const liste = this.listeRelances?.data?.listeResultats ?? [];

        //Indique si au moins une relance est sélectionnée
        return liste.some(r => !!r.isSelected);
    }

    /**
     * Indique si toutes les relances de la liste sont sélectionnées.
     *
     * @return true si tout est sélectionné, sinon false
     */
    private isAllSelected(): boolean {
        const liste = this.listeRelances?.data?.listeResultats ?? [];

        //Indique si toutes les relances affichées sont sélectionnées
        return liste.length > 0 && liste.every(r => !!r.isSelected);
    }

    /**
     * Exécute l’action "relancer" pour toutes les relances sélectionnées.
     */
    private executeSelection(): void {
        //Extraction de la sélection courante
        const selection = (this.listeRelances?.data?.listeResultats ?? []).filter(r => !!r.isSelected);

        //Exécution relance par relance (1 appel par idRelance)
        selection.forEach(relance => {
            this.notificationRelancesService.executeRelance(relance.idRelance)
                .pipe(first())
                .subscribe(res => {
                    //Gestion du code erreur du back
                    if (res?.codeErreur === TypeCodeErreur.NO_ERROR) {
                        this.toastrService.success(this.translateService.instant('global.success.generic'));
                    } else {
                        TypeCodeErreur.showError(res?.codeErreur, this.translateService, this.toastrService);
                    }
                });
        });
    }
}

